import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import './CreateEventModal.css';
import Toast from '../Toast';

const baseURL = process.env.REACT_APP_API_BASE_URL;
const API_KEY = process.env.REACT_APP_BROWSER_API_KEY;

async function uploadViaSignedUrl(file, folder, assetType) {
  const res = await axios.post(
    `${baseURL}/upload-urls`,
    { files: [{ name: file.name, type: file.type || 'image/jpeg' }], folder, bucket: 'original', metadata: { asset_type: assetType } },
    { headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY } }
  );
  const item = res.data?.uploadUrls?.[0];
  if (!item?.url) throw new Error('No signed URL returned');
  await axios.put(item.url, file, { headers: { 'Content-Type': file.type || 'image/jpeg' } });
  return item.publicUrl || item.url.split('?')[0];
}

const PRIVACY_OPTIONS = [
  {
    id: 'personal',
    label: 'Personal Event',
    desc: 'Private event for close circle',
    privacyType: 'private',
    isClosedCircle: 'CC',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
      </svg>
    ),
  },
  {
    id: 'public',
    label: 'Public Event',
    desc: 'Open and visible to everyone',
    privacyType: 'public',
    isClosedCircle: 'P',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
      </svg>
    ),
  },
  {
    id: 'professional',
    label: 'Professional Event',
    desc: 'Private, professional network only',
    privacyType: 'private',
    isClosedCircle: 'CCP',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
        <line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/>
      </svg>
    ),
  },
  {
    id: 'public_view',
    label: 'Public View',
    desc: 'Private event with public visibility',
    privacyType: 'private',
    isClosedCircle: 'PV',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
      </svg>
    ),
  },
];

export default function CreateEventModal({ onClose, onSuccess }) {
  const [form, setForm] = useState({
    event_name: '',
    privacy: '',
    event_type_id: '',
    start_date: '',
    end_date: '',
    hide_photos: '',
    restricted: '',
  });
  const [errors, setErrors] = useState({});
  const [eventTypes, setEventTypes] = useState([]);
  const [eventTypesLoading, setEventTypesLoading] = useState(false);
  const [etQuery, setEtQuery] = useState('');
  const [etOpen, setEtOpen] = useState(false);
  const etRef = useRef(null);
  const [coverPhoto, setCoverPhoto] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [coverUrl, setCoverUrl] = useState(null);
  const [coverUploading, setCoverUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState({ open: false, type: 'info', message: '' });
  const coverInputRef = useRef(null);

  const set = (key, val) => {
    setForm(prev => ({ ...prev, [key]: val }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: '' }));
  };

  useEffect(() => {
    if (!form.privacy) return;
    const selected = PRIVACY_OPTIONS.find(o => o.id === form.privacy);
    if (!selected) return;
    setEventTypes([]);
    setForm(prev => ({ ...prev, event_type_id: '' }));
    setEtQuery('');
    setEtOpen(false);
    setEventTypesLoading(true);
    fetch(
      `${baseURL}/event_type?privacyType=${selected.privacyType}&isClosedCircle=${selected.isClosedCircle}`,
      { headers: { 'x-api-key': API_KEY } }
    )
      .then(r => r.json())
      .then(data => setEventTypes(Array.isArray(data) ? data : (data.event_type ?? data.data ?? data.eventTypes ?? [])))
      .catch(() => setEventTypes([]))
      .finally(() => setEventTypesLoading(false));
  }, [form.privacy]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (etRef.current && !etRef.current.contains(e.target)) setEtOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredEt = eventTypes.filter(et =>
    et.toLowerCase().includes(etQuery.toLowerCase())
  );

  const selectEventType = (et) => {
    set('event_type_id', et);
    setEtQuery(et);
    setEtOpen(false);
  };

  const validate = () => {
    const e = {};
    const name = form.event_name.trim();
    if (!name) e.event_name = 'Event name is required';
    else if (name.length < 6) e.event_name = 'Event name must be at least 6 characters';
    else if (name.length > 80) e.event_name = 'Event name must be 80 characters or less';
    if (!form.privacy) e.privacy = 'Select an event privacy type';
    if (coverUploading) e.cover_photo = 'Please wait for cover photo to finish uploading';
    else if (!coverUrl) e.cover_photo = coverPhoto ? 'Cover photo upload failed. Please try again.' : 'Cover photo is required';
    if (form.privacy && eventTypes.length > 0 && !form.event_type_id) e.event_type_id = 'Select an event type';
    if (!form.start_date) e.start_date = 'Start date is required';
    if (!form.hide_photos) e.hide_photos = 'Select an option';
    if (!form.restricted) e.restricted = 'Select an option';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleCoverChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCoverPhoto(file);
    setCoverPreview(URL.createObjectURL(file));
    setErrors(prev => ({ ...prev, cover_photo: '' }));
    setCoverUploading(true);
    try {
      const publicUrl = await uploadViaSignedUrl(file, 'covers', 'cover_photo');
      setCoverUrl(publicUrl);
    } catch (err) {
setToast({ open: true, type: 'error', message: 'Cover photo upload failed. Please try again.' });
    } finally {
      setCoverUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!validate() || isSubmitting) return;
    const userId = localStorage.getItem('uid');
    if (!userId) {
      setToast({ open: true, type: 'error', message: 'Session expired. Please log in again.' });
      return;
    }
    setIsSubmitting(true);
    try {
      const selectedPrivacy = PRIVACY_OPTIONS.find(o => o.id === form.privacy);
      const payload = {
        source: 'photographer',
        type: form.event_type_id,
        title: form.event_name.trim(),
        from_date: new Date(form.start_date).toISOString(),
        to_date: form.end_date ? new Date(form.end_date).toISOString() : '',
        privacyType: selectedPrivacy?.privacyType,
        isClosedCircle: selectedPrivacy?.isClosedCircle,
        cover_photo: coverUrl || '',
        created_by: userId,
        status: 'active',
        restricted: form.restricted === 'yes',
        hide_photographer_photos: form.hide_photos === 'yes',
      };
      const res = await fetch(`${baseURL}/v2/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to create event');
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      setToast({ open: true, type: 'error', message: err.message || 'Failed to create event' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="ce-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="ce-sheet">
        <Toast open={toast.open} type={toast.type} message={toast.message} onClose={() => setToast(t => ({ ...t, open: false }))} />

        {/* Header */}
        <div className="ce-header">
          <h2 className="ce-title">Create New Event</h2>
          <button className="ce-close-btn" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="ce-body">

          {/* 1. Event Name */}
          <div className="ce-field-group">
            <label className="ce-label">Event Name <span className="ce-required">*</span></label>
            <input
              className={`ce-input${errors.event_name ? ' ce-input--error' : ''}`}
              type="text"
              placeholder="e.g. Riya & Arjun's Wedding"
              value={form.event_name}
              maxLength={80}
              onChange={e => set('event_name', e.target.value)}
            />
            <div className="ce-input-footer">
              {errors.event_name
                ? <span className="ce-error">{errors.event_name}</span>
                : <span className="ce-field-hint">Minimum 6 characters</span>
              }
              <span className={`ce-char-count${form.event_name.length > 74 ? ' ce-char-count--warn' : ''}`}>
                {form.event_name.length}/80
              </span>
            </div>
          </div>

          {/* 2. Event Privacy */}
          <div className="ce-field-group">
            <label className="ce-label">Event Privacy <span className="ce-required">*</span></label>
            <p className="ce-field-hint">Choose who can see and access this event</p>
            <div className="ce-privacy-cards">
              {PRIVACY_OPTIONS.map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  className={`ce-privacy-card${form.privacy === opt.id ? ' ce-privacy-card--active' : ''}`}
                  onClick={() => set('privacy', opt.id)}
                >
                  {opt.icon}
                  <span>
                    <span className="ce-privacy-title">{opt.label}</span>
                    <span className="ce-privacy-desc">{opt.desc}</span>
                  </span>
                </button>
              ))}
            </div>
            {errors.privacy && <span className="ce-error">{errors.privacy}</span>}

            {/* Event Types */}
            {form.privacy && (
              <div className="ce-event-types-wrap">
                {eventTypesLoading ? (
                  <div className="ce-event-types-loading">
                    <span className="ce-spinner" />
                    Loading event types…
                  </div>
                ) : eventTypes.length > 0 ? (
                  <>
                    <label className="ce-label" style={{ marginBottom: 0 }}>
                      Event Type <span className="ce-required">*</span>
                    </label>
                    <div className="ce-autocomplete" ref={etRef}>
                      <div className={`ce-autocomplete-input-wrap${errors.event_type_id ? ' ce-input--error' : ''}`}>
                        <input
                          className="ce-autocomplete-input"
                          type="text"
                          placeholder="Search event type…"
                          value={etQuery}
                          onChange={e => {
                            setEtQuery(e.target.value);
                            setForm(prev => ({ ...prev, event_type_id: '' }));
                            setEtOpen(true);
                            if (errors.event_type_id) setErrors(prev => ({ ...prev, event_type_id: '' }));
                          }}
                          onFocus={() => setEtOpen(true)}
                        />
                        {etQuery ? (
                          <button
                            type="button"
                            className="ce-autocomplete-clear"
                            onClick={() => { setEtQuery(''); setForm(prev => ({ ...prev, event_type_id: '' })); setEtOpen(true); }}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" width="14" height="14">
                              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                          </button>
                        ) : (
                          <svg className="ce-autocomplete-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" width="14" height="14">
                            <polyline points="6 9 12 15 18 9"/>
                          </svg>
                        )}
                      </div>
                      {etOpen && (
                        <ul className="ce-autocomplete-dropdown">
                          {filteredEt.length > 0 ? filteredEt.map(et => (
                            <li
                              key={et}
                              className={`ce-autocomplete-option${form.event_type_id === et ? ' ce-autocomplete-option--selected' : ''}`}
                              onMouseDown={() => selectEventType(et)}
                            >
                              {et}
                              {form.event_type_id === et && (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" width="14" height="14">
                                  <polyline points="20 6 9 17 4 12"/>
                                </svg>
                              )}
                            </li>
                          )) : (
                            <li className="ce-autocomplete-empty">No results for "{etQuery}"</li>
                          )}
                        </ul>
                      )}
                    </div>
                    {errors.event_type_id && <span className="ce-error">{errors.event_type_id}</span>}
                  </>
                ) : (
                  <p className="ce-field-hint" style={{ color: '#9ca3af' }}>No event types available for this privacy setting.</p>
                )}
              </div>
            )}
          </div>

          {/* 3. Event Date */}
          <div className="ce-field-group">
            <label className="ce-label">Event Date <span className="ce-required">*</span></label>
            <div className="ce-date-row">
              <div className="ce-date-wrap">
                <label className="ce-date-label">Start Date</label>
                <input
                  className={`ce-input${errors.start_date ? ' ce-input--error' : ''}`}
                  type="date"
                  value={form.start_date}
                  onChange={e => set('start_date', e.target.value)}
                />
                {errors.start_date && <span className="ce-error">{errors.start_date}</span>}
              </div>
              <div className="ce-date-wrap">
                <label className="ce-date-label">End Date <span className="ce-optional">(optional)</span></label>
                <input
                  className="ce-input"
                  type="date"
                  value={form.end_date}
                  min={form.start_date}
                  onChange={e => set('end_date', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* 5. Cover Photo */}
          <div className="ce-field-group">
            <label className="ce-label">Cover Photo <span className="ce-required">*</span></label>
            <input ref={coverInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleCoverChange} />
            {errors.cover_photo && <span className="ce-error">{errors.cover_photo}</span>}
            {!coverPreview ? (
              <button type="button" className="ce-upload-btn" onClick={() => coverInputRef.current.click()}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
                  <polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/><path d="M5 21h14"/>
                </svg>
                Upload Cover Photo
              </button>
            ) : (
              <div className="ce-cover-preview-wrap">
                <img src={coverPreview} alt="Cover" className="ce-cover-preview" />
                <div className="ce-cover-info">
                  <span className="ce-cover-name">{coverPhoto?.name}</span>
                  {coverUploading
                    ? <span className="ce-cover-status ce-cover-status--uploading">Uploading…</span>
                    : coverUrl
                      ? <span className="ce-cover-status ce-cover-status--done">✓ Uploaded</span>
                      : null
                  }
                  <button
                    type="button"
                    className="ce-cover-remove"
                    onClick={() => { setCoverPhoto(null); setCoverPreview(null); setCoverUrl(null); coverInputRef.current.value = ''; }}
                  >Remove</button>
                </div>
              </div>
            )}
          </div>

          {/* 6. Hide Photos */}
          <div className="ce-field-group">
            <label className="ce-label">Hide Photos <span className="ce-required">*</span></label>
            <p className="ce-field-hint">Should photos be hidden until you make them visible?</p>
            <div className="ce-toggle-row">
              <button type="button" className={`ce-toggle-btn${form.hide_photos === 'yes' ? ' ce-toggle-btn--active' : ''}`} onClick={() => set('hide_photos', 'yes')}>Yes</button>
              <button type="button" className={`ce-toggle-btn${form.hide_photos === 'no' ? ' ce-toggle-btn--active' : ''}`} onClick={() => set('hide_photos', 'no')}>No</button>
            </div>
            {errors.hide_photos && <span className="ce-error">{errors.hide_photos}</span>}
          </div>

          {/* 7. Restricted */}
          <div className="ce-field-group">
            <label className="ce-label">Restricted <span className="ce-required">*</span></label>
            <p className="ce-field-hint">Should access to this event be restricted?</p>
            <div className="ce-toggle-row">
              <button type="button" className={`ce-toggle-btn${form.restricted === 'yes' ? ' ce-toggle-btn--active' : ''}`} onClick={() => set('restricted', 'yes')}>Yes</button>
              <button type="button" className={`ce-toggle-btn${form.restricted === 'no' ? ' ce-toggle-btn--active' : ''}`} onClick={() => set('restricted', 'no')}>No</button>
            </div>
            {errors.restricted && <span className="ce-error">{errors.restricted}</span>}
          </div>

        </div>

        {/* Footer */}
        <div className="ce-footer">
          <button className="ce-cancel-btn" onClick={onClose} disabled={isSubmitting}>Cancel</button>
          <button className="ce-submit-btn" onClick={handleSubmit} disabled={isSubmitting || coverUploading}>
            {isSubmitting ? 'Creating…' : coverUploading ? 'Uploading…' : 'Create Event'}
          </button>
        </div>
      </div>
    </div>
  );
}
